import React, { useState, useEffect } from "react"
import { useHistory } from "react-router-dom"
import firebase from "firebase"
import moment from "moment"
import algoliasearch from "algoliasearch"
import {
	InstantSearch,
	Configure,
	connectSearchBox,
	connectHits,
	connectStateResults,
} from "react-instantsearch-dom"

import { db, auth } from "../../../utils/firebase"
import { SearchIcon } from "../../../utils/svgs"
import { AMENDMENT_CATEGORIES } from "../../../utils/exports/amendments"
import { logActivity } from "../../enquiry/_components/activity/log"
import { mintAmendmentReference } from "./reference"

import Window from "../../../components/structure/window/window"
import WindowCard from "../../../components/ui/window-card/window-card"
import Input from "../../../components/ui/inputs/input"
import Textarea from "../../../components/ui/inputs/textarea"
import Select from "../../../components/ui/select/select"
import Button from "../../../components/ui/button/button"

const searchClient = algoliasearch("HY764WYKNV", "059a9cd79a33ec2b2822acf2ffdfd4c9")

const SearchBox = ({ currentRefinement, refine }) => (
	<form noValidate action="" role="search">
		<Input
			value={currentRefinement}
			icon={<SearchIcon />}
			fullEvent={true}
			placeholder="Search bookings — reference, client, location"
			onChange={(e) => refine(e.currentTarget.value)}
		/>
	</form>
)
const CustomSearchBox = connectSearchBox(SearchBox)

// Bookings carry a short `reference` field that's displayed with a
// `GHDB-` prefix (see e.g. gift-vouchers/rows/voucher.js). Falls back
// to the Firestore doc id for legacy bookings that pre-date the field.
function pickBookingReference(hit) {
	if (hit.reference) return `GHDB-${hit.reference}`
	return hit.objectID
}

function pickClientName(hit) {
	return [hit.first_name, hit.last_name].filter(Boolean).join(" ")
}

function pickBookingLocation(hit) {
	return hit.booked_location || hit.enquiry?.location || ""
}

function BookingHit({ hit, onSelect }) {
	const dateFrom = hit.date_from?._seconds
		? moment(hit.date_from._seconds, "X").format("MMM D, YYYY")
		: "—"
	const reference = pickBookingReference(hit)
	const clientName = pickClientName(hit)
	const location = pickBookingLocation(hit)
	return (
		<button
			type="button"
			className="amendments-booking-hit"
			onClick={() => onSelect(hit)}>
			<span className="amendments-booking-hit-ref">{reference}</span>
			<span className="amendments-booking-hit-meta">
				{clientName || hit.email || "—"} · {location || "—"} · {dateFrom}
			</span>
		</button>
	)
}

const Hits = ({ hits, onSelect }) =>
	hits.length === 0 ? null : (
		<div className="amendments-booking-hits">
			{hits.slice(0, 8).map((hit) => (
				<BookingHit
					key={hit.objectID}
					hit={hit}
					onSelect={onSelect}
				/>
			))}
		</div>
	)
const CustomHits = connectHits(Hits)

const Results = connectStateResults(({ searchState, searchResults, onSelect }) => {
	if (!searchState.query) return <p className="amendments-booking-hint">Type to search bookings.</p>
	if (!searchResults || searchResults.nbHits === 0) {
		return <p className="amendments-booking-hint">No bookings match "{searchState.query}".</p>
	}
	return <CustomHits onSelect={onSelect} />
})

export default function NewAmendmentWindow({ close }) {
	const history = useHistory()

	const [stage, setStage] = useState("PICK_BOOKING")
	const [booking, setBooking] = useState(null)
	const [openAmendments, setOpenAmendments] = useState([])
	const [loadingOpen, setLoadingOpen] = useState(false)

	const [category, setCategory] = useState(null)
	const [categoryOther, setCategoryOther] = useState("")
	const [summary, setSummary] = useState("")
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(null)

	useEffect(() => {
		if (!booking) return
		setLoadingOpen(true)
		db.collection("amendments")
			.where("bookingId", "==", booking.objectID)
			.where("status", "==", "open")
			.get()
			.then((snap) => {
				setOpenAmendments(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
			})
			.catch((err) => {
				console.error("Unable to load open amendments for booking", err)
				setOpenAmendments([])
			})
			.finally(() => setLoadingOpen(false))
	}, [booking])

	const handleBookingSelect = (hit) => {
		setBooking(hit)
		setStage("CONFIRM_OR_FORM")
	}

	const handleContinueExisting = (amendmentId) => {
		close()
		history.push(`/amendments/${amendmentId}`)
	}

	const canSave =
		!!booking &&
		!!category &&
		(category !== "other" || categoryOther.trim().length > 0) &&
		summary.trim().length > 0

	const handleCreate = async () => {
		if (!canSave) return
		setSaving(true)
		setError(null)
		try {
			const reference = await mintAmendmentReference()
			const bookingReference = pickBookingReference(booking)
			const newDoc = await db.collection("amendments").add({
				reference,
				bookingId: booking.objectID,
				bookingReference,
				clientId: booking.client || null,
				status: "open",
				awaitingClient: false,
				awaitingSupplier: false,
				category,
				categoryOther: category === "other" ? categoryOther.trim() : null,
				summary: summary.trim(),
				assignedTo: null,
				priceDelta: null,
				resolvedAt: null,
				threadIds: [],
				createdBy: auth.currentUser?.uid || null,
				createdAt: firebase.firestore.FieldValue.serverTimestamp(),
			})

			await logActivity(
				`bookings/${booking.objectID}`,
				"AMENDMENT_OPENED",
				"Amendment opened against booking",
				{ link_to: `/amendments/${newDoc.id}`, link_label: reference },
			)

			close()
			history.push(`/amendments/${newDoc.id}`)
		} catch (err) {
			console.error("Unable to create amendment", err)
			setError(err.message || "Unable to create amendment.")
			setSaving(false)
		}
	}

	return (
		<Window
			className="amendments-new-window"
			title="New amendment"
			subtitle="Find the booking this amendment lives against, then create the record."
			close={close}>
			{stage === "PICK_BOOKING" && (
				<WindowCard
					title="Find a booking"
					className="has-top-margin">
					<InstantSearch
						searchClient={searchClient}
						indexName="bookings">
						<Configure filters="removed:FALSE" />
						<CustomSearchBox />
						<Results onSelect={handleBookingSelect} />
					</InstantSearch>
				</WindowCard>
			)}

			{stage === "CONFIRM_OR_FORM" && booking && (
				<>
					<WindowCard
						title="Selected booking"
						className="has-top-margin">
						<p className="amendments-selected-booking">
							<strong>{pickBookingReference(booking)}</strong>
							<br />
							<span>{pickClientName(booking) || booking.email || "—"}</span>
							<br />
							<span>
								{pickBookingLocation(booking) || "—"} ·{" "}
								{booking.date_from?._seconds
									? moment(booking.date_from._seconds, "X").format("MMM D, YYYY")
									: "—"}
							</span>
						</p>
						<Button
							type="OUTLINE"
							small={true}
							label="Pick a different booking"
							onClick={() => {
								setBooking(null)
								setOpenAmendments([])
								setStage("PICK_BOOKING")
							}}
						/>
					</WindowCard>

					{loadingOpen ? (
						<WindowCard className="has-top-margin">
							<small>Checking for open amendments on this booking…</small>
						</WindowCard>
					) : openAmendments.length > 0 ? (
						<WindowCard
							title="Open amendments on this booking"
							className="has-top-margin">
							<p>
								<small>
									Same-topic follow-ups should be added to an existing amendment rather than opening a new one. Different-topic changes get their own.
								</small>
							</p>
							<ul className="amendments-existing-list">
								{openAmendments.map((amendment) => (
									<li key={amendment.id}>
										<button
											type="button"
											className="amendments-existing-item"
											onClick={() => handleContinueExisting(amendment.id)}>
											<span className="amendments-existing-ref">{amendment.reference}</span>
											<span className="amendments-existing-meta">
												{AMENDMENT_CATEGORIES[amendment.category] || amendment.category} · {amendment.summary}
											</span>
										</button>
									</li>
								))}
							</ul>
						</WindowCard>
					) : null}

					<WindowCard
						title="New amendment details"
						className="has-top-margin">
						<div className="input-grid cols-1">
							<Select
								placeholder="Category:"
								value={category}
								selected={category}
								onSelect={(option) => setCategory(option.option)}
								options={AMENDMENT_CATEGORIES}
							/>

							{category === "other" && (
								<Input
									type="text"
									label="What kind of change?"
									value={categoryOther}
									onChange={setCategoryOther}
									placeholder="Describe the category in a few words"
								/>
							)}

							<Textarea
								placeholder="Short summary, e.g. &quot;Add 1 traveller, dates unchanged&quot;"
								value={summary}
								onChange={setSummary}
							/>
						</div>

						{error && <p className="amendments-error">{error}</p>}

						<Button
							label="Create amendment"
							loading={saving}
							loadingText="Creating…"
							onClick={handleCreate}
							className="window-card-button"
							disabled={!canSave || saving}
						/>
					</WindowCard>
				</>
			)}
		</Window>
	)
}
